import { useState, useRef, useEffect } from 'react';
import { Menu, MenuItem, ClickAwayListener, Popper, Paper, Grow, ListSubheader } from '@mui/material';
import { IconCalendar, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subWeeks,
  subYears,
  format,
  subDays,
} from 'date-fns';
import { DayPicker } from 'react-day-picker';
import styles from 'react-day-picker/dist/style.module.css';

import { Button } from 'ui';

import { useDashboard } from '../../store/dashboard/hooks';
import { useAccount } from '../../store/account/hooks';
import { getAllTimeRange, getPreviousPeriodRange, getTimeInSeconds, NO_COMPARISON_RANGE } from '../../core/utils';

const dayPickerClassNames = {
  ...styles,
  root: `px-6 py-3`,
  caption: `text-xl`,
  caption_label: `${styles.caption_label} !border-none`,
  dropdown: `${styles.dropdown} focus-visible:outline-4`,
  head_cell: `text-sm font-semibold text-gray-600 h-7`,
  day_selected: `!bg-primary-700 !text-white-100 rounded-lg`,
  cell: `h-9 w-9 p-0.25`,
  day: `w-8 h-8 rounded-lg !text-base`,
  day_today: `!font-semibold`,
  button: `enabled:hover:bg-primary-50 enabled:cursor-pointer disabled:opacity-50`,
  day_range_middle: ``,
};

const dateRangeKeys = {
  today: 'Today',
  last7Days: 'Last 7 Days',
  lastWeek: 'Last Week',
  last30Days: 'Last 30 Days',
  lastMonth: 'Last Month',
  last6Months: 'Last 6 Months',
  lastYear: 'Last Year',
  allTime: 'All Time',
  custom: 'Custom Range',
};

const comparisonRangeKeys = {
  previousPeriod: 'Previous Period',
  previousPeriodYoY: 'Previous Period YoY',
  custom: 'Custom Comparison',
};

const DateRangePicker = () => {
  const { account } = useAccount();
  const [anchorEl, setAnchorEl] = useState(null);
  const [datePickerState, setDatePickerState] = useState(null);
  const anchorRef = useRef(null);
  const [selectedRangeKey, setSelectedRangeKey] = useState(dateRangeKeys.allTime);
  const [selectedComparisonRangeKey, setSelectedComparisonRangeKey] = useState(comparisonRangeKeys.previousPeriod);
  const { setDateRange, setComparisonDateRange, startDate, endDate, comparisonStartDate, comparisonEndDate } =
    useDashboard();
  const [pickerRange, setPickerRange] = useState();
  const didAnchorAllTimeRange = useRef(false);

  const menuOpen = Boolean(anchorEl);

  const accountStartedAt = account?.onboardedAt || account?.createdAt;

  // The store is created before the account request resolves, so the default
  // 'All Time' range starts at a fallback floor. Re-anchor it to the tenant's own
  // onboarding date once the account lands — but only while both selections are
  // still untouched, so we never overwrite a range the user picked in the meantime.
  useEffect(() => {
    if (didAnchorAllTimeRange.current || !accountStartedAt) return;

    didAnchorAllTimeRange.current = true;

    if (selectedRangeKey !== dateRangeKeys.allTime || selectedComparisonRangeKey !== comparisonRangeKeys.previousPeriod)
      return;

    const allTimeRange = getAllTimeRange(account);
    setDateRange(allTimeRange);
    setComparisonDateRange(NO_COMPARISON_RANGE);
  }, [account, accountStartedAt, selectedRangeKey, selectedComparisonRangeKey, setDateRange, setComparisonDateRange]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseDatePicker = () => {
    setDatePickerState(null);
  };

  const updateDateRange = (range, comparisonRange) => {
    let newStartDate = startDate;
    let newEndDate = endDate;

    switch (range) {
      case dateRangeKeys.today:
        newStartDate = getTimeInSeconds(startOfDay(new Date()));
        newEndDate = getTimeInSeconds(endOfDay(new Date()));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      case dateRangeKeys.last7Days: {
        const sevenDaysAgo = subDays(new Date(), 7);
        const yesterday = subDays(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfDay(sevenDaysAgo));
        newEndDate = getTimeInSeconds(endOfDay(yesterday));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.lastWeek: {
        const lastWeek = subWeeks(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfWeek(lastWeek));
        newEndDate = getTimeInSeconds(endOfWeek(lastWeek));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.last30Days: {
        const thirtyDaysAgo = subDays(new Date(), 30);
        const yesterday = subDays(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfDay(thirtyDaysAgo));
        newEndDate = getTimeInSeconds(endOfDay(yesterday));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.lastMonth: {
        const lastMonth = subMonths(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfMonth(lastMonth));
        newEndDate = getTimeInSeconds(endOfMonth(lastMonth));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.last6Months: {
        const sixMonthAgo = subMonths(new Date(), 6);
        const lastMonth = subMonths(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfMonth(sixMonthAgo));
        newEndDate = getTimeInSeconds(endOfMonth(lastMonth));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.lastYear: {
        const lastYear = subYears(new Date(), 1);
        newStartDate = getTimeInSeconds(startOfYear(lastYear));
        newEndDate = getTimeInSeconds(endOfYear(lastYear));
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        break;
      }
      case dateRangeKeys.allTime: {
        const allTimeRange = getAllTimeRange(account);
        newStartDate = allTimeRange.startDate;
        newEndDate = allTimeRange.endDate;
        setDateRange(allTimeRange);
        break;
      }
    }

    // 'All Time' already spans everything the tenant has, so there is no period
    // before it to compare against and the widgets show no trend. An explicit
    // custom comparison is the one exception — those dates are the user's choice.
    if (range === dateRangeKeys.allTime) {
      if (comparisonRange !== comparisonRangeKeys.custom) setComparisonDateRange(NO_COMPARISON_RANGE);
      return;
    }

    // Calculate and set the comparison period
    switch (comparisonRange) {
      case comparisonRangeKeys.previousPeriod: {
        setComparisonDateRange(getPreviousPeriodRange(newStartDate, newEndDate));
        break;
      }
      case comparisonRangeKeys.previousPeriodYoY: {
        const oneYearAgoStart = subYears(new Date(newStartDate * 1000), 1);
        const oneYearAgoEnd = subYears(new Date(newEndDate * 1000), 1);
        const comparisonStartDate = getTimeInSeconds(startOfDay(oneYearAgoStart));
        const comparisonEndDate = getTimeInSeconds(endOfDay(oneYearAgoEnd));
        setComparisonDateRange({ comparisonStartDate, comparisonEndDate });
        break;
      }
    }
  };

  const handleChange = (rangeKey) => {
    if (selectedRangeKey === dateRangeKeys.custom) {
      if (rangeKey === dateRangeKeys.custom) {
        // Restore the selected custom range
        setPickerRange({ from: new Date(startDate * 1000), to: new Date(endDate * 1000) });
      } else {
        // Reset range date picker when selecting any option but "Custom range"
        setPickerRange(undefined);
      }
    } else if (selectedComparisonRangeKey === comparisonRangeKeys.custom) {
      // If the selected comparison range is custom, we need to reset the date range
      setPickerRange(undefined);
    }

    setSelectedRangeKey(rangeKey);

    if (rangeKey === dateRangeKeys.custom) {
      setDatePickerState('dateRange');
    } else {
      updateDateRange(rangeKey, selectedComparisonRangeKey);
    }

    handleCloseMenu();
  };

  const handleComparisonChange = (comparisonRangeKey) => {
    if (selectedComparisonRangeKey === comparisonRangeKeys.custom) {
      if (comparisonRangeKey === comparisonRangeKeys.custom) {
        // Restore the selected custom comparison range
        setPickerRange({ from: new Date(comparisonStartDate * 1000), to: new Date(comparisonEndDate * 1000) });
      } else {
        // Reset range date picker when selecting any option but "Custom comparison"
        setPickerRange(undefined);
      }
    } else if (selectedRangeKey === dateRangeKeys.custom) {
      // If the selected range is custom, we need to reset the comparison range
      setPickerRange(undefined);
    }

    if (selectedComparisonRangeKey !== comparisonRangeKeys.custom) setSelectedComparisonRangeKey(comparisonRangeKey);

    if (comparisonRangeKey === comparisonRangeKeys.custom) {
      setDatePickerState('comparisonRange');
    } else {
      updateDateRange(selectedRangeKey, comparisonRangeKey);
    }

    handleCloseMenu();
  };

  const handleSelectDateRange = (range, selectedDay) => {
    if (!range || (pickerRange?.from && pickerRange?.to)) {
      setPickerRange({ from: selectedDay, to: undefined });
      return;
    }
    setPickerRange(range);
    if (range?.from && range?.to) {
      const startDate = getTimeInSeconds(range.from);
      const endDate = getTimeInSeconds(range.to);

      if (datePickerState === 'dateRange') {
        setDateRange({ startDate, endDate });

        // Calculate and set the comparison period
        switch (selectedComparisonRangeKey) {
          case comparisonRangeKeys.previousPeriod: {
            setComparisonDateRange(getPreviousPeriodRange(startDate, endDate));
            break;
          }
          case comparisonRangeKeys.previousPeriodYoY: {
            const oneYearAgoStart = subYears(new Date(startDate * 1000), 1);
            const oneYearAgoEnd = subYears(new Date(endDate * 1000), 1);
            const comparisonStartDate = getTimeInSeconds(startOfDay(oneYearAgoStart));
            const comparisonEndDate = getTimeInSeconds(endOfDay(oneYearAgoEnd));
            setComparisonDateRange({ comparisonStartDate, comparisonEndDate });
            break;
          }
        }
      }

      if (datePickerState === 'comparisonRange') {
        setComparisonDateRange({ comparisonStartDate: startDate, comparisonEndDate: endDate });
      }

      setTimeout(() => setDatePickerState(null), 500);
    }
  };

  return (
    <>
      <div className="flex space-x-3">
        {(selectedRangeKey !== dateRangeKeys.allTime ||
          selectedComparisonRangeKey !== comparisonRangeKeys.previousPeriod) && (
          <div className="text-right text-sm">
            <div>
              {`${format(new Date(startDate * 1000), 'MM/dd/yyyy')} - ${format(new Date(endDate * 1000), 'MM/dd/yyyy')}`}
            </div>
            <div>
              vs{' '}
              {comparisonStartDate && comparisonEndDate
                ? `${format(new Date(comparisonStartDate * 1000), 'MM/dd/yyyy')} - ${format(new Date(comparisonEndDate * 1000), 'MM/dd/yyyy')}`
                : 'N/A'}
            </div>
          </div>
        )}

        <Button
          title={selectedRangeKey}
          LeftIcon={IconCalendar}
          RightIcon={menuOpen ? IconChevronUp : IconChevronDown}
          onClick={handleClick}
          className="min-w-[180px] !justify-start !text-left"
          titleClassName="flex-1"
          ref={anchorRef}
        />
      </div>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        classes={{ paper: 'w-[360px] !rounded-lg mt-0.5 !shadow-custom', list: '!grid !grid-flow-col !grid-rows-8' }}
      >
        {Object.values(dateRangeKeys).map((range) => (
          <MenuItem
            key={range}
            classes={{
              root: '!py-2 !px-3.5 !w-[180px]',
              selected: '!bg-white-100 !text-primary-700',
            }}
            onClick={() => handleChange(range)}
            selected={selectedRangeKey === range}
          >
            <span className="text-base">{range}</span>
          </MenuItem>
        ))}
        <ListSubheader className="!px-3.5 !py-2 text-caption1 font-semibold uppercase !leading-tight !text-gray-950">
          Comparison
        </ListSubheader>
        {Object.values(comparisonRangeKeys).map((range) => (
          <MenuItem
            key={range}
            classes={{
              root: '!py-2 !px-3.5 !w-[180px]',
              selected: '!bg-white-100 !text-primary-700',
            }}
            onClick={() => handleComparisonChange(range)}
            selected={selectedComparisonRangeKey === range}
          >
            <span className="text-base">{range}</span>
          </MenuItem>
        ))}
      </Menu>

      <Popper
        open={!!datePickerState}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        className="z-[100]"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper classes={{ root: '!rounded-lg my-0.5 !shadow-custom' }}>
              <ClickAwayListener onClickAway={handleCloseDatePicker}>
                <div>
                  <DayPicker
                    mode="range"
                    selected={pickerRange}
                    onSelect={handleSelectDateRange}
                    classNames={dayPickerClassNames}
                    fromDate={new Date('2023-01-02')}
                    toDate={new Date()}
                    captionLayout="dropdown"
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default DateRangePicker;
